import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { langDate } from "../../../../../utils/properties";
import Filter from "../../../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import { buildQueryString, formatNumber, showError } from "../../../../../utils/utilities";
import { useParams } from "react-router-dom";
import { setFilters } from "../../../../../redux/ducks/Filters";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { Table } from "antd";
import ProjectInvoiceStatement from "../../../../../services/api/ProjectInvoiceStatement";
import dayjs from "dayjs";

const Monthly = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentDate = dayjs().format("YYYY");
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const params = useParams();
  const [state, setState] = useState({
    tableContent: [],
    loading: false,
    filterLoading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    const queryString = buildQueryString({}, { year: currentDate });
    dispatch(setFilters(queryString));
    getData(params.id, queryString.string);
  }, []);

  const getData = async (id, queryString) => {
    dispatch(setLoading(true));
    const data = await ProjectInvoiceStatement.getDataMonthly(id, queryString);
    if (!data.error) {
      data.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data.data,
      }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const formikFilter = useFormik({
    initialValues: {
      year: dayjs(currentDate, "YYYY"),
    },
    validate: (values) => {
      let errors = {};
      if (!values.year) errors.year = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const year = dayjs(values.year, "YYYY").format("YYYY");
      getData(params.id, `year=${year}`);
    },
  });

  const fieldsFilter = [
    {
      type: "year",
      isRequired: true,
      id: "year",
      name: "year",
      format: "YYYY",
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectsManagement.incomeStatement.filter.year"),
      value: formikFilter.values.year,
      onChange: (date) => {
        formikFilter.setFieldValue("year", date);
      },
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({}, { year: currentDate });
    formikFilter.setFieldValue("year", dayjs(queryString.obj.year, "YYYY"));
    dispatch(setFilters(queryString));
    getData(params.id, queryString.string);
  };

  const columns = [
    {
      title: t("projectsManagement.incomeStatement.yearlyStatement"),
      dataIndex: "activity",
      width: 200,
      fixed: "left",
      render: (activity, record) => {
        let name = "";
        switch (record.type) {
          case "REVENUE":
            name = t("projectsManagement.incomeStatement.revenue");
            break;
          case "VALUE_INTERNAL":
            name = t("projectsManagement.incomeStatement.internal");
            break;
          case "VALUE_EXTERNAL":
            name = t("projectsManagement.incomeStatement.external");
            break;
          case "LABOR":
            name = t("projectsManagement.incomeStatement.labor");
            break;
          case "PERCENT_LABOR":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "PERCENT_EXPENSE":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "PERCENT_MARGIN":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "EXPENSE":
            name = t("projectsManagement.incomeStatement.expense");
            break;
          case "COSTS":
            name = t("projectsManagement.incomeStatement.costs");
            break;
          case "MARGIN":
            name = t("projectsManagement.incomeStatement.margin");
            break;
          default:
            name = activity;
            break;
        }
        return name;
      },
    },
    {
      title: t("projectsManagement.incomeStatement.JANUARY"),
      dataIndex: ["values", "JANUARY"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.FEBRUARY"),
      dataIndex: ["values", "FEBRUARY"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.MARCH"),
      dataIndex: ["values", "MARCH"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.APRIL"),
      dataIndex: ["values", "APRIL"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.MAY"),
      dataIndex: ["values", "MAY"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },

    {
      title: t("projectsManagement.incomeStatement.JUNE"),
      dataIndex: ["values", "JUNE"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.JULY"),
      dataIndex: ["values", "JULY"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.AUGUST"),
      dataIndex: ["values", "AUGUST"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.SEPTEMBER"),
      dataIndex: ["values", "SEPTEMBER"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.OCTOBER"),
      dataIndex: ["values", "OCTOBER"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.NOVEMBER"),
      dataIndex: ["values", "NOVEMBER"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
    {
      title: t("projectsManagement.incomeStatement.DECEMBER"),
      dataIndex: ["values", "DECEMBER"],
      width: 80,
      className: "table-value-right",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },

    {
      title: t("projectsManagement.incomeStatement.TOT"),
      dataIndex: ["values", "TOT"],
      width: 80,
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          decimalScale:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? 1
              : 0,
          value: amount,
          lang: currentLanguage,
          suffix:
            record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
              ? "PERCENT"
              : "EUR",
        }),
    },
  ];

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        loading={state.filterLoading}
        allInOneRow={false}
        onReset={resetFilter}
      />
      <Table
        bordered
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={false}
        scroll={{ x: 1500 }}
        rowClassName={(record) => record.type}
      />
    </>
  );
};

export default Monthly;
